'use client'

import dynamic from 'next/dynamic'
import Link from 'next/link'
import { alpha } from '@mui/material/styles'
import { Box, Container, Typography } from '@mui/material'

// Types
import type { PartnersContactCtaProps } from '@/types/components/organisms/partners'

// Utils
import { localizeHref } from '@/utils/theme/nav-menu'

// Components
const RevealAtom = dynamic(() => import('@/components/atoms/animation/reveal'))
const EyebrowTypographyAtom = dynamic(() => import('@/components/atoms/typography/eyebrow'))
const PrimaryButtonAtom = dynamic(() => import('@/components/atoms/button/primary'))
const MenuItemIconAtom = dynamic(() => import('@/components/atoms/icon/menu-item'))

// Invitation to get in touch: a warm gold band with the contact button and the sales phone side by side
const PartnersContactCtaOrganism = (props: PartnersContactCtaProps) => {
	// Props
	const { lng, eyebrow, title, subtitle, primary, phoneLabel, phone, phoneHref } = props

	return (
		<Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
			<RevealAtom>
				<Box
					sx={{
						position: 'relative',
						overflow: 'hidden',
						borderRadius: { xs: 6, md: 10 },
						px: { xs: 3, md: 8 },
						py: { xs: 6, md: 9 },
						textAlign: 'center',
						bgcolor: t => alpha(t.palette.secondary.main, 0.12),
						border: t => `1px solid ${alpha(t.palette.secondary.main, 0.3)}`
					}}
				>
					{/* Soft rings behind the text */}
					{[320, 520].map(size => (
						<Box
							key={size}
							sx={{
								position: 'absolute',
								top: '50%',
								left: '50%',
								width: size,
								height: size,
								translate: '-50% -50%',
								borderRadius: '50%',
								border: t => `1px solid ${alpha(t.palette.secondary.main, 0.25)}`,
								pointerEvents: 'none'
							}}
						/>
					))}

					<Box sx={{ position: 'relative', maxWidth: 640, mx: 'auto' }}>
						<EyebrowTypographyAtom text={eyebrow} />

						<Typography component="h2" sx={{ mt: 2.5, fontSize: { xs: 26, md: 36 }, fontWeight: 800, lineHeight: 1.5 }}>
							{title}
						</Typography>

						<Typography sx={{ mt: 2, fontSize: { xs: 16, md: 17 }, lineHeight: 2, color: 'text.secondary' }}>{subtitle}</Typography>

						<Box sx={{ mt: 5, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', justifyContent: 'center', gap: { xs: 2.5, sm: 4 } }}>
							<Link href={localizeHref(lng, primary.href)}>
								<PrimaryButtonAtom text={primary.text} color="secondary" size="large" sx={{ px: 5 }} />
							</Link>

							<Box component="a" href={phoneHref} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'text.primary', textDecoration: 'none' }}>
								<Box
									sx={{
										display: 'grid',
										placeItems: 'center',
										width: 44,
										height: 44,
										borderRadius: '50%',
										bgcolor: 'common.white',
										color: 'secondary.main',
										'& .MuiSvgIcon-root': { fontSize: 22 }
									}}
								>
									<MenuItemIconAtom name="call" />
								</Box>

								<Box sx={{ textAlign: 'start' }}>
									<Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{phoneLabel}</Typography>
									<Typography sx={{ fontSize: 18, fontWeight: 800 }}>{phone}</Typography>
								</Box>
							</Box>
						</Box>
					</Box>
				</Box>
			</RevealAtom>
		</Container>
	)
}

export default PartnersContactCtaOrganism
